/**
 * collision.ts — Pure ground-plane collision (replaces Phaser Arcade Physics).
 *
 * The game is entirely on flat ground, so all collision is 2D: entities are
 * circles, buildings are axis-aligned boxes, and the world is a rectangle. That
 * keeps this module dependency-free and unit-testable, and keeps the runtime cost
 * near zero on mobile.
 *
 * Rects are {x, y, w, h} with x/y at the top-left (same shape as systems/level.ts).
 */
import type { Rect } from './level';
import type { Vec2 } from '../utils/vec2';

/**
 * Keep a circle of `radius` fully inside a rectangle. Mutates `pos`.
 *
 * Takes a rect rather than a width and height because the ground the walk
 * happens on is no longer the whole map: the town carries on past it as
 * scenery, so the rectangle to be held inside starts at an offset (see
 * `LevelSpec.bounds`). Clamping rather than blocking is deliberate — the walker
 * slides along the boundary instead of sticking to it, which is what makes an
 * edge you can't cross feel like an edge rather than a bug.
 */
export function clampToBounds(pos: Vec2, radius: number, bounds: Rect): Vec2 {
  pos.x = Math.min(Math.max(pos.x, bounds.x + radius), bounds.x + bounds.w - radius);
  pos.y = Math.min(Math.max(pos.y, bounds.y + radius), bounds.y + bounds.h - radius);
  return pos;
}

/**
 * Push a circle out of an axis-aligned box if it overlaps.
 *
 * Finds the closest point on the box to the circle centre; if that point is nearer
 * than `radius`, the circle is displaced along the shortest escape axis. Returns
 * true when a correction was applied. Mutates `pos`.
 */
export function resolveCircleRect(pos: Vec2, radius: number, r: Rect): boolean {
  const closestX = Math.min(Math.max(pos.x, r.x), r.x + r.w);
  const closestY = Math.min(Math.max(pos.y, r.y), r.y + r.h);
  const dx = pos.x - closestX;
  const dy = pos.y - closestY;

  if (dx * dx + dy * dy > radius * radius) return false; // clear of the box

  if (dx !== 0 || dy !== 0) {
    // Outside the box but touching a face/corner — push straight out along the
    // vector from the closest point.
    const dist = Math.hypot(dx, dy);
    pos.x = closestX + (dx / dist) * radius;
    pos.y = closestY + (dy / dist) * radius;
    return true;
  }

  // Centre is *inside* the box: eject via whichever face is nearest.
  const toLeft = pos.x - r.x;
  const toRight = r.x + r.w - pos.x;
  const toTop = pos.y - r.y;
  const toBottom = r.y + r.h - pos.y;
  const min = Math.min(toLeft, toRight, toTop, toBottom);

  if (min === toLeft) pos.x = r.x - radius;
  else if (min === toRight) pos.x = r.x + r.w + radius;
  else if (min === toTop) pos.y = r.y - radius;
  else pos.y = r.y + r.h + radius;
  return true;
}

/** Resolve a circle against every box, returning true if any pushed it. */
export function resolveCircleRects(pos: Vec2, radius: number, rects: readonly Rect[]): boolean {
  let hit = false;
  for (const r of rects) {
    if (resolveCircleRect(pos, radius, r)) hit = true;
  }
  return hit;
}

/**
 * Push two overlapping circles apart until they just touch. Mutates both.
 *
 * `aShare` is the fraction of the correction applied to `a` (the rest moves `b`),
 * which is how the heavier body gets to shove the lighter one: 0.5 splits it
 * evenly, 0 makes `a` immovable, 1 makes `b` immovable. Returns true if they were
 * overlapping. Perfectly concentric centres are separated along +x, since there's
 * no meaningful direction to choose.
 */
export function resolveCircleCircle(
  a: Vec2,
  ra: number,
  b: Vec2,
  rb: number,
  aShare = 0.5,
): boolean {
  const minDist = ra + rb;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distSq = dx * dx + dy * dy;
  if (distSq >= minDist * minDist) return false;

  const dist = Math.sqrt(distSq);
  // Exactly co-located: no meaningful direction, so pick one rather than
  // dividing by zero. The overlap is the full separation distance.
  const ux = dist === 0 ? 1 : dx / dist;
  const uy = dist === 0 ? 0 : dy / dist;
  const overlap = minDist - dist;
  a.x -= ux * overlap * aShare;
  a.y -= uy * overlap * aShare;
  b.x += ux * overlap * (1 - aShare);
  b.y += uy * overlap * (1 - aShare);
  return true;
}

/**
 * Push a circle out of a **static** circular obstacle — a tree trunk. Only `pos`
 * moves; the obstacle is immovable and passed as plain numbers so a caller can
 * hand it straight from readonly config without risking a stray mutation.
 *
 * Returns true if a correction was applied.
 */
export function resolveCircleObstacle(
  pos: Vec2,
  radius: number,
  ox: number,
  oy: number,
  obstacleRadius: number,
): boolean {
  const minDist = radius + obstacleRadius;
  const dx = pos.x - ox;
  const dy = pos.y - oy;
  const distSq = dx * dx + dy * dy;
  if (distSq >= minDist * minDist) return false;

  const dist = Math.sqrt(distSq);
  if (dist === 0) {
    // Dead centre on the trunk — no meaningful direction, so pick one.
    pos.x = ox + minDist;
    return true;
  }
  pos.x = ox + (dx / dist) * minDist;
  pos.y = oy + (dy / dist) * minDist;
  return true;
}

/** Do two circles overlap? Used for hazard/car contact checks. */
export function circlesOverlap(a: Vec2, ra: number, b: Vec2, rb: number): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const r = ra + rb;
  return dx * dx + dy * dy <= r * r;
}

/**
 * Does a circle overlap an axis-aligned box? Cars are boxes, so this is the
 * player/dog ↔ car hit test.
 */
export function circleRectOverlap(pos: Vec2, radius: number, r: Rect): boolean {
  const closestX = Math.min(Math.max(pos.x, r.x), r.x + r.w);
  const closestY = Math.min(Math.max(pos.y, r.y), r.y + r.h);
  const dx = pos.x - closestX;
  const dy = pos.y - closestY;
  return dx * dx + dy * dy <= radius * radius;
}
